import { FieldValues, Path, useFormContext } from "react-hook-form";
import { ObrConfiguration, ObrPinGeneration } from "../../OBRSettings";
import * as React from "react";
import { useLanguage } from "../../Language";
import { Box, FormControl, InputLabel, Typography } from "@mui/material";
import { FormTextField } from "@tlc/forms/FormTextField";

interface ConfirmPINFieldProps<T extends FieldValues> {
  name: Path<T>;
  confirmName: Path<T>;
  pinLengthRequired?: number | null;
  pinLabel?: string;
  confirmPinLabel?: string;
  pinMismatchError?: string;
  disabled?: boolean;
}

export const ConfirmPINField = <T extends FieldValues>({
  name,
  confirmName,
  pinLengthRequired,
  pinLabel,
  confirmPinLabel,
  pinMismatchError,
  disabled,
}: ConfirmPINFieldProps<T>) => {
  const { translate } = useLanguage();
  const { formState, getFieldState, getValues, trigger } = useFormContext<T>();

  const { isTouched: isConfirmPINTouched } = getFieldState(
    confirmName,
    formState,
  );

  const pinLengthText = pinLengthRequired
    ? pinLengthRequired.toString()
    : "1-8";

  return (
    <>
      <FormTextField<T>
        name={name}
        required
        disabled={disabled}
        options={{
          validate: (value: string) => {
            if (isConfirmPINTouched) {
              void trigger(confirmName);
            }

            if (pinLengthRequired && value.length !== pinLengthRequired) {
              return translate("pinLengthOutOfRange", {
                PINLENGTH: pinLengthText,
              });
            }

            if (!pinLengthRequired && (value.length < 1 || value.length > 8)) {
              return translate("pinLengthOutOfRange", {
                PINLENGTH: pinLengthText,
              });
            }

            return true;
          },
        }}
        helperText={translate("ci_PINLengthHint", {
          PINLENGTH: pinLengthText,
        })}
        label={pinLabel ?? translate("ci_PIN")}
        type="password"
      />

      <FormTextField<T>
        required
        name={confirmName}
        disabled={disabled}
        options={{
          validate: (value: string) => {
            if (value !== getValues(name)) {
              return (
                pinMismatchError ?? translate("newPINConfirmPINMustMatchError")
              );
            }

            return true;
          },
        }}
        label={confirmPinLabel ?? translate("ci_confirmPIN")}
        type="password"
      />
    </>
  );
};

export interface PinFieldProps<T extends FieldValues> {
  obrConfiguration: ObrConfiguration;
  name: Path<T>;
  confirmName: Path<T>;
}

interface ReadOnlyFieldProps {
  label: string;
  value: string;
}
const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => {
  return (
    <FormControl fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <Box
        sx={{
          padding: "8px 16px", // Aligns with typical TextField padding
          fontSize: "1rem",
          lineHeight: "1.5",
          color: "rgba(0, 0, 0, 0.87)",
        }}
        role="textbox"
        aria-readonly="true"
      >
        <Typography>{value}</Typography>
      </Box>
    </FormControl>
  );
};

// OBR specific component, rename OBRPINField?
export const PinField = <T extends FieldValues>({
  name,
  confirmName,
  obrConfiguration,
}: PinFieldProps<T>) => {
  const { translate } = useLanguage();

  if (obrConfiguration.howToGeneratePin === ObrPinGeneration.None) {
    return (
      <ConfirmPINField<T>
        name={name}
        confirmName={confirmName}
        pinLengthRequired={obrConfiguration.pinLengthRequired}
      />
    );
  }

  if (obrConfiguration.howToGeneratePin === ObrPinGeneration.Last4OfPhone) {
    return (
      <ReadOnlyField
        label={translate("ci_PIN")}
        value={translate("obrPINWasGeneratedWithPhone")}
      />
    );
  }

  if (obrConfiguration.howToGeneratePin === ObrPinGeneration.Birthday) {
    return (
      <ReadOnlyField
        label={translate("ci_PIN")}
        value={translate("obrPINWasGeneratedWithDOB")}
      />
    );
  }
};
